import React, { useState } from 'react';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import {
	testDowngradeSubscription,
	testDowngradeToFree,
	testFinalizeCancellation,
} from '../../services/subscription';

function DowngradeScenario({ scenario, title, description, buttonLabel, isLoading, onRun }) {
	return (
		<article className="border rounded-3 p-3 h-100 d-flex flex-column">
			<span className="badge text-bg-light border text-dark align-self-start mb-2">
				Scenario {scenario}
			</span>
			<h4 className="h6">{title}</h4>
			<p className="small text-muted flex-grow-1 mb-3">{description}</p>
			<button
				type="button"
				className="btn btn-danger btn-sm w-100"
				disabled={isLoading}
				onClick={onRun}
			>
				{isLoading ? 'Running...' : buttonLabel}
			</button>
		</article>
	);
}

function Developer() {
	const [loading, setLoading] = useState({
		sync_plan: false,
		non_recurring_expiry: false,
		scheduled_free_downgrade: false,
		immediate_downgrade: false,
		recurring_cycle_end: false,
		recurring_scheduled_free_downgrade: false,
		recurring_immediate_downgrade: false,
		finalize_cancellation: false,
	});

	const handleSyncPlans = async () => {
		try {
			setLoading((prev) => ({
				...prev,
				sync_plan: true,
			}));

			await axiosInstanceV2.get('/razorpay/sync-plans');
			toast.success('Plans synced successfully.');
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Unable to sync plans.');
		} finally {
			setLoading((prev) => ({
				...prev,
				sync_plan: false,
			}));
		}
	};

	const runSubscriptionTest = async (loadingKey, request, successMessage) => {
		try {
			setLoading((prev) => ({
				...prev,
				[loadingKey]: true,
			}));

			await request();
			toast.success(successMessage);
			window.setTimeout(() => window.location.reload(), 900);
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Subscription test could not be completed.');
		} finally {
			setLoading((prev) => ({
				...prev,
				[loadingKey]: false,
			}));
		}
	};

	return (
		<main className="container py-4">
			<header className="mb-4">
				<div className="d-flex align-items-center gap-2 mb-1">
					<h2 className="h4 mb-0">Developer tools</h2>
					<span className="badge text-bg-secondary">Internal</span>
				</div>
				<p className="text-muted mb-0">Run operational checks and subscription test scenarios.</p>
			</header>

			<div className="row g-4 mb-4">
				<div className="col-12 col-md-6">
					<section className="card h-100 shadow-sm border-danger-subtle">
						<div className="card-body">
							<h3 className="h5 card-title">Sentry testing</h3>
							<p className="card-text text-muted small">
								Send a deliberate error to confirm Sentry monitoring is receiving events.
							</p>
							<button
								type="button"
								className="btn btn-outline-danger"
								onClick={() => {
									throw new Error('Sentry Test');
								}}
							>
								Send Sentry test error
							</button>
						</div>
					</section>
				</div>

				<div className="col-12 col-md-6">
					<section className="card h-100 shadow-sm border-primary-subtle">
						<div className="card-body">
							<h3 className="h5 card-title">Subscription plan sync</h3>
							<p className="card-text text-muted small">
								Refresh plan information from Razorpay before testing a subscription flow.
							</p>
							<button
								type="button"
								className="btn btn-outline-primary"
								disabled={loading.sync_plan}
								onClick={handleSyncPlans}
							>
								{loading.sync_plan ? 'Syncing plans...' : 'Sync subscription plans'}
							</button>
						</div>
					</section>
				</div>
			</div>

			<div className="row g-4 mb-4">
				<div className="col-12">
					<section className="card shadow-sm border-warning-subtle">
						<div className="card-header bg-light">
							<h3 className="h5 mb-1">Scheduled cancellation</h3>
							<p className="mb-0 small text-muted">
								Test the "cancel at cycle end" flow without waiting for the real billing cycle to
								end.
							</p>
						</div>
						<div className="card-body">
							<p className="small text-muted mb-3">
								First, from the subscription settings page, click{' '}
								<strong>Cancel Subscription</strong> to schedule a cancellation (this never calls
								Razorpay — it just flags the row and queues a job for <code>current_end</code>).
								Then use the button below to fire that job immediately instead of waiting: it calls
								Razorpay's cancel API for real, which triggers the{' '}
								<code>subscription.cancelled</code> webhook and moves the account to Free — same
								code path as production, just not waiting on the clock.
							</p>
							<button
								type="button"
								className="btn btn-warning btn-sm"
								disabled={loading.finalize_cancellation}
								onClick={() =>
									runSubscriptionTest(
										'finalize_cancellation',
										testFinalizeCancellation,
										'Finalize-cancellation job queued — check worker logs / Bull Board for the Razorpay cancel + webhook chain.',
									)
								}
							>
								{loading.finalize_cancellation ? 'Running...' : 'Run finalize cancellation now'}
							</button>
							<p className="small text-muted mt-2 mb-0">
								To test undoing it instead, click <strong>Resume Subscription</strong> on the
								settings page before running this — it just deletes the queued job, nothing to run
								here.
							</p>
						</div>
					</section>
				</div>
			</div>

			<div
				className="alert alert-warning small mb-4"
				role="alert"
			>
				<strong>Heads up:</strong> downgrade tests change the current account immediately and reload
				the page after a successful request. Select the section that matches its payment type.
			</div>

			<div className="row g-4">
				<div className="col-12 col-xl-6">
					<section className="card shadow-sm h-100">
						<div className="card-header bg-light">
							<h3 className="h5 mb-1">Non-recurring payment</h3>
							<p className="mb-0 small text-muted">Use for one-time subscriptions.</p>
						</div>
						<div className="card-body">
							<div className="row g-3">
								<div className="col-12 col-md-4">
									<DowngradeScenario
										scenario="1"
										title="Plan expiry"
										description="Active paid plan that should move to Free when its validity ends."
										buttonLabel="Run expiry downgrade"
										isLoading={loading.non_recurring_expiry}
										onRun={() =>
											runSubscriptionTest(
												'non_recurring_expiry',
												testDowngradeToFree,
												'Non-recurring subscription downgraded to Free.',
											)
										}
									/>
								</div>
								<div className="col-12 col-md-4">
									<DowngradeScenario
										scenario="2"
										title="Scheduled Free downgrade"
										description="Cancelled plan that is already scheduled to move to Free."
										buttonLabel="Run scheduled downgrade"
										isLoading={loading.scheduled_free_downgrade}
										onRun={() =>
											runSubscriptionTest(
												'scheduled_free_downgrade',
												testDowngradeToFree,
												'Scheduled downgrade to Free completed.',
											)
										}
									/>
								</div>
								<div className="col-12 col-md-4">
									<DowngradeScenario
										scenario="3"
										title="Immediate plan downgrade"
										description="Plan already scheduled to move to another paid plan."
										buttonLabel="Run immediate downgrade"
										isLoading={loading.immediate_downgrade}
										onRun={() =>
											runSubscriptionTest(
												'immediate_downgrade',
												testDowngradeSubscription,
												'Subscription downgrade completed.',
											)
										}
									/>
								</div>
							</div>
						</div>
					</section>
				</div>

				<div className="col-12 col-xl-6">
					<section className="card shadow-sm h-100">
						<div className="card-header bg-light">
							<h3 className="h5 mb-1">Recurring payment</h3>
							<p className="mb-0 small text-muted">Use for auto-renewing subscriptions.</p>
						</div>
						<div className="card-body">
							<div className="row g-3">
								<div className="col-12 col-md-4">
									<DowngradeScenario
										scenario="1"
										title="Cycle-end Free downgrade"
										description="Recurring plan cancelled at cycle end that should now move to Free."
										buttonLabel="Run cycle-end downgrade"
										isLoading={loading.recurring_cycle_end}
										onRun={() =>
											runSubscriptionTest(
												'recurring_cycle_end',
												testDowngradeToFree,
												'Recurring subscription downgraded to Free.',
											)
										}
									/>
								</div>
								<div className="col-12 col-md-4">
									<DowngradeScenario
										scenario="2"
										title="Scheduled Free downgrade"
										description="Recurring plan already scheduled to downgrade to Free."
										buttonLabel="Run scheduled downgrade"
										isLoading={loading.recurring_scheduled_free_downgrade}
										onRun={() =>
											runSubscriptionTest(
												'recurring_scheduled_free_downgrade',
												testDowngradeToFree,
												'Scheduled recurring downgrade to Free completed.',
											)
										}
									/>
								</div>
								<div className="col-12 col-md-4">
									<DowngradeScenario
										scenario="3"
										title="Immediate plan downgrade"
										description="Recurring plan scheduled to move to another paid plan."
										buttonLabel="Run immediate downgrade"
										isLoading={loading.recurring_immediate_downgrade}
										onRun={() =>
											runSubscriptionTest(
												'recurring_immediate_downgrade',
												testDowngradeSubscription,
												'Recurring subscription downgrade completed.',
											)
										}
									/>
								</div>
							</div>
						</div>
					</section>
				</div>
			</div>
		</main>
	);
}

export default Developer;
